import { motion } from "framer-motion";
import { Check, CalendarCheck, Calculator, Mail } from "lucide-react";
import { CALENDLY_URL, GRAD_BTN } from "@/components/Navbar";

const EASE = [0.16, 1, 0.3, 1];

const COMPTA = [
  "Suivi des comptes de la copropriété en temps réel",
  "Approbation des dépenses en ligne, en toute sécurité",
  "Charges et budgets en un coup d'œil",
  "Export comptable en un clic",
  "Paramétrage adapté à votre organisation",
];

const COPRO = [
  "Téléchargement gratuit de l'application Mobile iOS/Android",
  "Webapp toute plateforme incluse",
  "Support client avec numéro dédié",
  "Assistance en ligne par chatbot 24h/24 et 7j/7",
  "Guide d'installation inclus",
];

const SYNDIC = [
  "Téléchargement gratuit de l'application Mobile iOS/Android",
  "Webapp toute plateforme et en multi-licences incluse",
  "Mise à jour gratuite et maintenance incluses",
  "Support client avec numéro dédié",
  "Assistance en ligne par chatbot 24h/24 et 7j/7",
  "Guide d'installation inclus",
];

const Plan = ({ gradient, badge, name, price, unit, items, testid, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.8, delay, ease: EASE }}
    whileHover={{ y: -6 }}
    className={`flex h-full flex-col rounded-3xl border p-9 transition-shadow duration-300 md:p-11 ${
      gradient
        ? "relative overflow-hidden border-0 bg-[linear-gradient(135deg,#2BC97E_0%,#0BC1D6_60%,#00C4CC_100%)] text-white shadow-[0_35px_80px_-30px_rgba(0,180,190,0.6)]"
        : "border-black/5 bg-white text-ink shadow-[0_20px_55px_-30px_rgba(45,52,54,0.3)] hover:shadow-[0_30px_70px_-30px_rgba(0,190,200,0.5)]"
    }`}
    data-testid={testid}
  >
    {gradient && (
      <div className="pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full bg-white/10 blur-2xl" />
    )}
    {badge && (
      <span className="mb-5 w-fit rounded-full bg-white/20 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-white">
        {badge}
      </span>
    )}
    <h3 className="text-lg font-bold">{name}</h3>
    <p className="mt-5 flex items-baseline gap-2">
      <span className="text-5xl font-bold md:text-6xl">{price}</span>
      <span className={`text-sm font-semibold ${gradient ? "text-white/75" : "text-ink/50"}`}>
        {unit}
      </span>
    </p>
    <ul className={`mt-8 flex-1 space-y-3.5 text-sm ${gradient ? "text-white/90" : "text-ink/70"}`}>
      {items.map((item) => (
        <li key={item} className="flex gap-3">
          <Check size={17} className={`mt-0.5 shrink-0 ${gradient ? "text-white" : "text-electric"}`} />
          {item}
        </li>
      ))}
    </ul>
    <a
      href={CALENDLY_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`${testid}-cta-button`}
      className={`mt-9 flex items-center justify-center gap-2.5 rounded-full px-8 py-4 text-sm font-bold transition-transform duration-300 hover:scale-105 active:scale-95 ${
        gradient
          ? "bg-white text-ink shadow-[0_15px_35px_-12px_rgba(0,60,70,0.4)]"
          : `text-white shadow-[0_15px_35px_-12px_rgba(0,190,200,0.6)] ${GRAD_BTN}`
      }`}
    >
      <CalendarCheck size={17} />
      Réserver une démo
    </a>
  </motion.div>
);

export const Pricing = () => (
  <section id="offres" className="bg-white py-24 lg:py-32" data-testid="pricing-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8, ease: EASE }}
        className="max-w-3xl"
      >
        <span className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-electric">
          <span className="h-px w-10 bg-electric" />
          Offres disponibles
        </span>
        <h2 className="mt-6 text-4xl font-bold tracking-normal leading-[1.15] text-ink md:text-5xl lg:text-6xl">
          Un prix simple,{" "}
          <span className="bg-[linear-gradient(100deg,#00C6CC,#2FCB74)] bg-clip-text text-transparent">
            par lot et par mois.
          </span>
        </h2>
      </motion.div>

      <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Tuile Comptabilité — mise en première position */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: EASE }}
          whileHover={{ y: -6 }}
          className="flex h-full flex-col rounded-3xl border border-black/5 bg-white p-9 text-ink shadow-[0_20px_55px_-30px_rgba(45,52,54,0.3)] transition-shadow duration-300 hover:shadow-[0_30px_70px_-30px_rgba(0,190,200,0.5)] md:p-11"
          data-testid="pricing-comptabilite"
        >
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[linear-gradient(100deg,#00C6CC,#2FCB74)] text-white shadow-[0_15px_35px_-12px_rgba(0,190,200,0.6)]">
            <Calculator size={26} />
          </span>
          <span className="mt-5 w-fit whitespace-nowrap max-[359px]:whitespace-normal rounded-full bg-electric/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-electric">
            Fonctionnalité en option
          </span>
          <h3 className="mt-3 text-lg font-bold">Comptabilité</h3>
          <p className="mt-3 text-sm leading-relaxed text-ink/60">
            La comptabilité de vos copropriétés, sans tableur. Suivez les
            comptes, les charges et les budgets en temps réel, faites approuver
            les dépenses en ligne et préparez vos exports comptables en un
            clic. Le module se paramètre selon votre organisation, du
            monopropriétaire au cabinet multi-copropriétés : nous établissons
            ensemble un devis sur mesure.
          </p>
          <p className="mt-5 text-2xl font-bold text-ink">
            Devis sur demande
          </p>
          <ul className="mt-6 flex-1 space-y-3.5 text-sm text-ink/70">
            {COMPTA.map((item) => (
              <li key={item} className="flex gap-3">
                <Check size={17} className="mt-0.5 shrink-0 text-electric" />
                {item}
              </li>
            ))}
          </ul>
          <a
            href="mailto:contact@syndicappli.fr?subject=Demande%20de%20devis%20%E2%80%94%20module%20Comptabilit%C3%A9"
            data-testid="pricing-comptabilite-cta-button"
            className="mt-9 flex items-center justify-center gap-2.5 rounded-full border border-ink px-8 py-4 text-sm font-bold text-ink transition-transform duration-300 hover:scale-105 hover:border-electric hover:text-electric active:scale-95"
          >
            <Mail size={17} />
            Demander un devis
          </a>
        </motion.div>
        <Plan
          testid="pricing-coproprietaires"
          name="Offre Copropriétaires"
          price="9€"
          unit="HT / lot / mois"
          items={COPRO}
          delay={0.1}
        />
        <Plan
          gradient
          testid="pricing-syndics"
          badge="Pour les syndics"
          name="Offre Syndics"
          price="8€"
          unit="HT / lot / mois"
          items={SYNDIC}
          delay={0.2}
        />
      </div>
    </div>
  </section>
);
